import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {EmpresasService} from '../../../superadmin/services/empresas.service';
import {moeda, SEGMENTOS_CRM} from '../crm-segmentos';

export interface RegrasCrmTela {
  fAlta: number;
  vMedio: number;
  vAlto: number;
  diasSumido: number;
  diasPerdido: number;
}

/** Painel lateral com os cinco números que definem os segmentos, e a regra de cada um ao vivo. */
@Component({
  selector: 'app-regras-crm',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './regras-crm.component.html',
  styleUrls: ['./regras-crm.component.scss']
})
export class RegrasCrmComponent implements OnChanges {
  @Input() aberto = false;
  @Input() regras: RegrasCrmTela;
  @Input() editadasEm: string = null;
  @Input() defaultCalculadoEm: string = null;
  @Output() fechar = new EventEmitter<void>();
  @Output() salvou = new EventEmitter<RegrasCrmTela & {recalculando: boolean}>();
  @Output() recalculou = new EventEmitter<void>();

  form: RegrasCrmTela = {fAlta: 6, vMedio: 40, vAlto: 80, diasSumido: 45, diasPerdido: 120};
  salvando = false;
  buscandoSugestao = false;
  erro = '';
  aviso = '';
  readonly segmentos = SEGMENTOS_CRM;

  constructor(private empresasService: EmpresasService) {}

  ngOnChanges(): void {
    if (this.aberto && this.regras) {
      this.form = {
        fAlta: Number(this.regras.fAlta), vMedio: Number(this.regras.vMedio), vAlto: Number(this.regras.vAlto),
        diasSumido: Number(this.regras.diasSumido), diasPerdido: Number(this.regras.diasPerdido)
      };
      this.erro = '';
      this.aviso = '';
    }
  }

  get origem(): string {
    if (this.editadasEm) return 'Regras ajustadas por você.';
    if (this.defaultCalculadoEm) return 'Faixas calculadas a partir dos pedidos da sua loja.';
    return 'Valores padrão. Quando a loja tiver 30 clientes com pedido em 90 dias, as faixas se ajustam sozinhas.';
  }

  /** Mesmas regras do backend (valideRegras), com a mensagem no campo que precisa mudar. */
  get errosPorCampo(): {[campo in keyof RegrasCrmTela]?: string} {
    const r = this.form;
    const erros: {[campo in keyof RegrasCrmTela]?: string} = {};
    if (!(r.fAlta >= 4)) erros.fAlta = 'Use 4 ou mais pedidos.';
    if (!(r.vMedio > 0)) erros.vMedio = 'Informe um valor maior que zero.';
    if (!(r.vAlto > r.vMedio)) erros.vAlto = 'Precisa ser maior que o ticket médio.';
    if (!(r.diasSumido >= 8)) erros.diasSumido = 'Use 8 dias ou mais.';
    if (!(r.diasPerdido > r.diasSumido)) erros.diasPerdido = 'Precisa ser maior que os dias para Sumido.';
    return erros;
  }

  get erroValidacao(): string {
    return Object.keys(this.errosPorCampo).length ? 'Corrija os campos marcados.' : '';
  }

  regraDo(codigo: string): string {
    const r = this.form;
    switch (codigo) {
      case 'CAMPEAO': return `${r.fAlta} ou mais pedidos em 90 dias e ticket médio a partir de ${moeda(r.vAlto)}, em dia com o ritmo.`;
      case 'FIEL': return '3 ou mais pedidos no histórico e comprando no ritmo de sempre.';
      case 'NOVO': return 'Até 2 pedidos no histórico e em dia.';
      case 'ESFRIANDO': return `Passou de 1,5 vez o próprio intervalo entre pedidos (mínimo 7 dias), até ${r.diasSumido} dias.`;
      case 'SUMIDO': return `De ${r.diasSumido + 1} a ${r.diasPerdido} dias sem pedir.`;
      case 'PERDIDO': return `Mais de ${r.diasPerdido} dias sem pedir.`;
      default: return 'Cadastrado, mas nunca fez pedido.';
    }
  }

  useSugestao(): void {
    this.buscandoSugestao = true;
    this.erro = '';
    this.aviso = '';
    this.empresasService.obtenhaDefaultSugeridoCrm().then((sugestao: any) => {
      this.form = {...this.form, fAlta: sugestao.fAlta, vMedio: sugestao.vMedio, vAlto: sugestao.vAlto};
      this.aviso = 'Preenchemos frequência e ticket com o que a sua base mostra. Confira e salve.';
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : 'Não foi possível calcular a sugestão agora.';
    }).finally(() => this.buscandoSugestao = false);
  }

  salve(): void {
    if (this.erroValidacao) {
      this.erro = this.erroValidacao;
      return;
    }
    this.salvando = true;
    this.erro = '';
    this.empresasService.atualizeRegrasCrm({
      crmFAlta: this.form.fAlta, crmVMedio: this.form.vMedio, crmVAlto: this.form.vAlto,
      crmDiasSumido: this.form.diasSumido, crmDiasPerdido: this.form.diasPerdido
    }).then((resposta: any) => {
      this.salvou.emit({...this.form, recalculando: !resposta || resposta.recalculando !== false});
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : 'Não foi possível salvar as regras.';
    }).finally(() => this.salvando = false);
  }

  recalcule(): void {
    this.salvando = true;
    this.erro = '';
    this.empresasService.recalculeCrm().then(() => {
      this.aviso = 'Recalculando todos os clientes. Os números atualizam em alguns minutos.';
      this.recalculou.emit();
    }).catch((erro: any) => {
      this.erro = typeof erro === 'string' ? erro : 'Não foi possível recalcular agora.';
    }).finally(() => this.salvando = false);
  }
}
